import type { SwipeDir } from '@mado/schema';

/**
 * Pointer handling.
 *
 * The rule that matters (DESIGN-SYSTEM §5): a swipe tracks the finger while it
 * moves and commits on release. Playing a canned animation after the fact is the
 * single clearest tell that a "mobile demo" is really a desktop widget, and it is
 * exactly what the incumbents' players do.
 */

/** Fraction of the viewport a swipe must cross to commit on distance alone. */
const COMMIT_RATIO = 0.25;
/** …or this much velocity, so a fast flick commits early. */
const COMMIT_VELOCITY = 0.45; // px per ms
/** Movement below this is a tap, not a drag. */
const SLOP = 8;

export interface GestureCallbacks {
  /** Live finger tracking. `dx`/`dy` are in CSS px from the drag origin. */
  onDragMove(dx: number, dy: number, axis: 'x' | 'y'): void;
  onDragEnd(committed: SwipeDir | null): void;
  /** Wheel or trackpad on a scroll step. */
  onWheel(deltaY: number): void;
  /** Phone walkthrough: a free drag (any direction) that looks around the picture. */
  onPan?(dx: number, dy: number): void;
  onPanEnd?(): void;
  /** Phone walkthrough: two fingers pinched; `ratio` < 1 pinched in (zoom out), > 1 spread. */
  onPinch?(ratio: number): void;
}

export interface GestureConfig {
  /** Which axes this step accepts; anything else is ignored so the page can scroll. */
  allowX: boolean;
  allowY: boolean;
  /**
   * Phone walkthrough: a drag the step does not use for a swipe looks around
   * the picture in any direction, and two fingers pinch to the whole screen
   * and back. The embed then owns touch in its box (it fills the phone).
   */
  pan?: boolean;
}

/** A pinch has to change the fingers' distance by this much to count. */
const PINCH_MIN = 0.12;

export class Gestures {
  private cfg: GestureConfig = { allowX: false, allowY: false };
  private active = false;
  private axis: 'x' | 'y' | 'free' | null = null;
  /** Fingers down while `pan` is on, for a pinch. */
  private readonly fingers = new Map<number, { x: number; y: number }>();
  private pinch: { d0: number; ratio: number } | null = null;
  private x0 = 0;
  private y0 = 0;
  private lastX = 0;
  private lastY = 0;
  private lastT = 0;
  private pointerId: number | null = null;

  constructor(
    private readonly host: HTMLElement,
    private readonly cb: GestureCallbacks,
  ) {
    host.addEventListener('pointerdown', this.down, { passive: true });
    host.addEventListener('pointermove', this.move, { passive: false });
    host.addEventListener('pointerup', this.up, { passive: true });
    host.addEventListener('pointercancel', this.up, { passive: true });
    host.addEventListener('wheel', this.wheel, { passive: false });
  }

  configure(cfg: GestureConfig): void {
    this.cfg = cfg;
    // Let the host page scroll on axes this step does not use, rather than
    // trapping the viewer inside the embed.
    this.host.style.touchAction = cfg.pan
      ? 'none'
      : cfg.allowY
        ? cfg.allowX
          ? 'none'
          : 'pan-x'
        : 'pan-y';
  }

  private spread(): number {
    const [a, b] = [...this.fingers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  private down = (e: PointerEvent): void => {
    if (this.cfg.pan) {
      this.fingers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.fingers.size === 2) {
        // A second finger: the gesture is a pinch now, not a drag.
        if (this.axis === 'free') this.cb.onPanEnd?.();
        this.active = false;
        this.axis = null;
        const d0 = this.spread();
        this.pinch = d0 > 0 ? { d0, ratio: 1 } : null;
        return;
      }
    }
    if (!this.cfg.allowX && !this.cfg.allowY && !this.cfg.pan) return;
    this.active = true;
    this.axis = null;
    this.pointerId = e.pointerId;
    this.x0 = this.lastX = e.clientX;
    this.y0 = this.lastY = e.clientY;
    this.lastT = e.timeStamp;
  };

  private move = (e: PointerEvent): void => {
    if (this.fingers.has(e.pointerId))
      this.fingers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pinch) {
      e.preventDefault();
      const d = this.spread();
      if (d > 0) this.pinch.ratio = d / this.pinch.d0;
      return;
    }
    if (!this.active || e.pointerId !== this.pointerId) return;
    const dx = e.clientX - this.x0;
    const dy = e.clientY - this.y0;

    if (!this.axis) {
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
      const wantsX = Math.abs(dx) > Math.abs(dy);
      // Claim only an axis this step actually uses; otherwise stand down so the
      // host page keeps its natural scrolling — or, in a walkthrough, look
      // around the picture.
      if (wantsX && this.cfg.allowX) this.axis = 'x';
      else if (!wantsX && this.cfg.allowY) this.axis = 'y';
      else if (this.cfg.pan) this.axis = 'free';
      else {
        this.active = false;
        return;
      }
    }

    e.preventDefault();
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.lastT = e.timeStamp;
    if (this.axis === 'free') this.cb.onPan?.(dx, dy);
    else this.cb.onDragMove(dx, dy, this.axis);
  };

  private up = (e: PointerEvent): void => {
    this.fingers.delete(e.pointerId);
    if (this.pinch) {
      // The pinch ends when the first finger lifts; the other is then ignored.
      if (this.fingers.size < 2) {
        const { ratio } = this.pinch;
        this.pinch = null;
        this.active = false;
        if (Math.abs(ratio - 1) >= PINCH_MIN) this.cb.onPinch?.(ratio);
      }
      return;
    }
    if (!this.active || e.pointerId !== this.pointerId) return;
    this.active = false;
    if (!this.axis) return;
    if (this.axis === 'free') {
      this.axis = null;
      this.cb.onPanEnd?.();
      return;
    }

    const dx = e.clientX - this.x0;
    const dy = e.clientY - this.y0;
    const along = this.axis === 'x' ? dx : dy;
    const extent = this.axis === 'x' ? this.host.clientWidth : this.host.clientHeight;

    // Instantaneous velocity from the last move sample, not the average over the
    // whole gesture: a slow drag that ends in a flick should commit, and an
    // average would swallow the flick entirely.
    const recent = this.axis === 'x' ? e.clientX - this.lastX : e.clientY - this.lastY;
    const velocity = Math.abs(recent) / Math.max(1, e.timeStamp - this.lastT);

    const committed =
      Math.abs(along) > extent * COMMIT_RATIO || velocity > COMMIT_VELOCITY
        ? this.axis === 'x'
          ? along < 0
            ? 'left'
            : 'right'
          : along < 0
            ? 'up'
            : 'down'
        : null;

    this.axis = null;
    this.cb.onDragEnd(committed as SwipeDir | null);
  };

  private wheel = (e: WheelEvent): void => {
    if (!this.cfg.allowY) return;
    e.preventDefault();
    this.cb.onWheel(e.deltaY);
  };
}
